/**
 * Mobile sticky add-to-cart bar. Delegates the actual add-to-cart action to
 * the real submit button in the main buy-buttons area (no duplicated
 * variant/cart logic), and mirrors its price/label/disabled state live via
 * MutationObserver so it always reflects the currently selected variant.
 */
class StickyAddToCart extends HTMLElement {
  #observer;
  #sourceObserver;
  #priceObserver;
  #buttonObserver;

  connectedCallback() {
    this.sourceSelector = this.getAttribute('source-selector');
    this.priceSelector = this.getAttribute('price-selector');

    this.sourceEl = document.querySelector(this.sourceSelector);
    this.realButton = this.sourceEl?.querySelector('[ref="addToCartButton"]');
    this.priceEl = document.querySelector(this.priceSelector);
    this.priceMirror = this.querySelector('[data-sticky-price]');
    this.stickyButton = this.querySelector('[data-sticky-button]');

    if (!this.sourceEl || !this.realButton || !this.stickyButton) return;

    this.stickyButton.addEventListener('click', this.#handleClick);

    this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0 });
    this.#observer.observe(this.sourceEl);

    if (this.priceEl && this.priceMirror) {
      this.#syncPrice();
      this.#priceObserver = new MutationObserver(this.#syncPrice);
      this.#priceObserver.observe(this.priceEl, { childList: true, subtree: true, characterData: true });
    }

    this.#syncButtonState();
    this.#buttonObserver = new MutationObserver(this.#syncButtonState);
    this.#buttonObserver.observe(this.realButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#priceObserver?.disconnect();
    this.#buttonObserver?.disconnect();
    this.stickyButton?.removeEventListener('click', this.#handleClick);
  }

  #handleIntersect = ([entry]) => {
    // Only reveal once the buy area has been scrolled past going down
    // (its top has moved above the viewport) — never before it's been reached.
    const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    this.classList.toggle('sticky-add-to-cart--visible', scrolledPast);
  };

  #syncPrice = () => {
    if (this.priceEl && this.priceMirror) {
      this.priceMirror.textContent = this.priceEl.textContent.trim();
    }
  };

  #syncButtonState = () => {
    const disabled = this.realButton.hasAttribute('disabled');
    this.stickyButton.disabled = disabled;
    this.stickyButton.toggleAttribute('aria-disabled', disabled);
  };

  #handleClick = () => {
    if (this.realButton && !this.realButton.disabled) {
      this.realButton.click();
    }
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
